import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { ClientProfile } from '../types';

interface Props {
  profile: ClientProfile;
  onUpdate: (data: Partial<ClientProfile>) => void;
}

const LEAD_SOURCES = ['Orgânico', 'Google Ads', 'Facebook Ads', 'Instagram', 'Indicação', 'LinkedIn', 'YouTube', 'Email Marketing', 'TikTok', 'Outro'];

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20';
const labelCls = 'block text-[11px] text-white/40 mb-1.5 font-500';

export default function MarketingTab({ profile, onUpdate }: Props) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedNotes, setSavedNotes] = useState(false);
  const [newTag, setNewTag] = useState('');

  const [form, setForm] = useState({
    lead_source: profile.lead_source || '',
    utm_source: profile.utm_source || '',
    utm_medium: profile.utm_medium || '',
    utm_campaign: profile.utm_campaign || '',
    rdstation_contact_id: profile.rdstation_contact_id || '',
    mailchimp_subscriber_id: profile.mailchimp_subscriber_id || '',
    marketing_opt_in: profile.marketing_opt_in ?? false,
  });

  const [notes, setNotes] = useState(profile.internal_notes || '');
  const [tags, setTags] = useState<string[]>(profile.internal_tags || []);

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const saveMarketing = async () => {
    setSaving(true);
    const { error } = await supabase.from('profiles').update({
      ...form,
      internal_tags: tags,
      updated_at: new Date().toISOString(),
    }).eq('id', profile.id);
    if (!error) {
      onUpdate({ ...form, internal_tags: tags });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    await supabase.from('profiles').update({ internal_notes: notes, updated_at: new Date().toISOString() }).eq('id', profile.id);
    onUpdate({ internal_notes: notes });
    setSavedNotes(true);
    setTimeout(() => setSavedNotes(false), 3000);
    setSavingNotes(false);
  };

  const addTag = () => {
    const t = newTag.trim();
    if (t && !tags.includes(t)) {
      setTags((prev) => [...prev, t]);
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => setTags((prev) => prev.filter((t) => t !== tag));

  return (
    <div className="grid grid-cols-2 gap-5">
      {/* Left: CRM & UTMs */}
      <div className="space-y-5">
        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4 flex items-center gap-2">
            <i className="ri-map-pin-2-line text-violet-400" />
            Origem & UTMs
          </h3>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Origem do lead</label>
              <select value={form.lead_source} onChange={(e) => set('lead_source', e.target.value)} className={inputCls}>
                <option value="" className="bg-[#1A1A2E]">Selecionar origem...</option>
                {LEAD_SOURCES.map((s) => <option key={s} value={s} className="bg-[#1A1A2E]">{s}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className={labelCls}>UTM Source</label>
                <input className={inputCls} value={form.utm_source} onChange={(e) => set('utm_source', e.target.value)} placeholder="google" />
              </div>
              <div>
                <label className={labelCls}>UTM Medium</label>
                <input className={inputCls} value={form.utm_medium} onChange={(e) => set('utm_medium', e.target.value)} placeholder="cpc" />
              </div>
              <div>
                <label className={labelCls}>UTM Campaign</label>
                <input className={inputCls} value={form.utm_campaign} onChange={(e) => set('utm_campaign', e.target.value)} placeholder="black-friday" />
              </div>
            </div>
          </div>
        </div>

        {/* RD Station */}
        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4 flex items-center gap-2">
            <i className="ri-global-line text-violet-400" />
            Integrações CRM
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-orange-500/8 border border-orange-500/20 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-500/20">
                  <i className="ri-fire-line text-orange-400 text-[14px]" />
                </div>
                <div>
                  <p className="text-[13px] font-600 text-orange-300">RD Station</p>
                  <p className="text-[11px] text-orange-400/60">ID do contato para integração futura</p>
                </div>
              </div>
              <input className={inputCls} value={form.rdstation_contact_id} onChange={(e) => set('rdstation_contact_id', e.target.value)} placeholder="ID do contato no RD Station" />
            </div>

            <div className="p-4 bg-amber-500/8 border border-amber-500/20 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-500/20">
                  <i className="ri-mail-send-line text-amber-400 text-[14px]" />
                </div>
                <div>
                  <p className="text-[13px] font-600 text-amber-300">Mailchimp</p>
                  <p className="text-[11px] text-amber-400/60">Subscriber ID para automações de e-mail</p>
                </div>
              </div>
              <input className={inputCls} value={form.mailchimp_subscriber_id} onChange={(e) => set('mailchimp_subscriber_id', e.target.value)} placeholder="Subscriber ID do Mailchimp" />
            </div>
          </div>
        </div>

        {/* Marketing opt-in */}
        <div className="bg-white/5 border border-white/8 rounded-xl p-4">
          <div
            onClick={() => set('marketing_opt_in', !form.marketing_opt_in)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className={`w-11 h-6 rounded-full transition-colors relative shrink-0 overflow-hidden ${form.marketing_opt_in ? 'bg-violet-600' : 'bg-white/20'}`}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${form.marketing_opt_in ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
            <div>
              <p className="text-[13px] font-600 text-white">Aceita comunicações de marketing</p>
              <p className="text-[11px] text-white/40">E-mails, newsletters, ofertas e novidades da plataforma</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={saveMarketing} disabled={saving} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
            {saving ? 'Salvando...' : 'Salvar dados CRM'}
          </button>
          {saved && <span className="text-emerald-400 text-[12px] flex items-center gap-1.5"><i className="ri-check-line" />Salvo!</span>}
        </div>
      </div>

      {/* Right: Tags + Notes */}
      <div className="space-y-5">
        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-4 flex items-center gap-2">
            <i className="ri-price-tag-3-line text-violet-400" />
            Tags Internas
          </h3>
          <div className="flex flex-wrap gap-1.5 mb-3 min-h-[36px]">
            {tags.map((tag) => (
              <span key={tag} className="flex items-center gap-1 text-[11px] bg-violet-600/20 text-violet-300 px-2.5 py-1 rounded-full border border-violet-500/20">
                {tag}
                <button onClick={() => removeTag(tag)} className="cursor-pointer hover:text-red-400 transition-colors ml-0.5 w-3 h-3 flex items-center justify-center">
                  <i className="ri-close-line text-[11px]" />
                </button>
              </span>
            ))}
            {!tags.length && <p className="text-[12px] text-white/20 italic">Nenhuma tag ainda</p>}
          </div>
          <div className="flex gap-2">
            <input
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
              placeholder="Ex: churned, vip, suporte..."
            />
            <button
              onClick={addTag}
              className="px-3 py-2 bg-violet-600/30 hover:bg-violet-600/50 text-violet-300 rounded-xl cursor-pointer transition-colors text-[13px] whitespace-nowrap"
            >
              <i className="ri-add-line" />
            </button>
          </div>
          <p className="text-[11px] text-white/20 mt-2">Pressione Enter ou clique + para adicionar. Tags salvas junto com dados CRM.</p>
        </div>

        <div className="bg-white/5 border border-white/8 rounded-xl p-5">
          <h3 className="text-[14px] font-600 text-white mb-1 flex items-center gap-2">
            <i className="ri-sticky-note-line text-violet-400" />
            Observações Internas
          </h3>
          <p className="text-[11px] text-white/30 mb-3">Visível apenas para a equipe administrativa. Não aparece para o cliente.</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={10}
            placeholder="Notas internas, histórico de atendimento, observações comerciais, ocorrências de suporte..."
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-white/30">{notes.length}/2000 caracteres</span>
            <div className="flex items-center gap-3">
              {savedNotes && <span className="text-emerald-400 text-[12px] flex items-center gap-1"><i className="ri-check-line" />Salvo!</span>}
              <button onClick={saveNotes} disabled={savingNotes} className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {savingNotes ? 'Salvando...' : 'Salvar notas'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
